import useHasAccess from "@hooks/useHasAccess"
import useIsDesktop from "@hooks/useIsDesktop"
import useNavigationLinks from "@hooks/useNavigationLinks"
import useRouter from "@hooks/useRouter"
import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import filterUserLinks from "@utils/filterUserLinks"

import { NavTopBar, ObjectsView, ViewBar, ViewMenu } from "@organisms"
import CardsView from "@organisms/ObjectsView/CardsView/CardsView"
import TableView from "@organisms/ObjectsView/TableView/TableView"
import ViewTypesProvider from "@organisms/ObjectsView/ViewTypesProvider/ViewTypesProvider"

import { AppFrame } from "@templates"

import { JOBS_ROUTES } from "@routes/jobs"

import { objectMap } from "@constants/objectMap"
import { PERMISSION_LEVEL } from "@constants/permissionLevel"
import {
    JOBS_SERIES_LIST_CONTROLS_ACTIVE_TAB,
    JOBS_SERIES_LIST_CONTROLS_KEYWORD_SEARCH,
    JOBS_SERIES_LIST_CONTROLS_PHASE,
    JOBS_SERIES_LIST_CONTROLS_VIEW,
} from "@constants/storage"

import { JOB_SERIES_CARDS_VIEW_ATTRIBUTE_OPTIONS, JOB_SERIES_CARDS_VIEW_SORT_OPTIONS } from "./JobSeriesList.constants"
import { JobSeriesObjectsViewTabs } from "./JobSeriesObjectsViewTabs"
import JobSeriesCardsView from "./views/JobSeriesCardsView/JobSeriesCardsView"
import JobSeriesTableView from "./views/JobSeriesTableView/JobSeriesTableView"

//
// import { faIcon } from "@utils/faIcon"

// import { Button } from "@atoms/index"

// import JobSeriesCreateDialog from "@pages/JobSeries/JobSeriesCreateDialog/JobSeriesCreateDialog"

function JobSeriesListContent() {
    const isDesktop = useIsDesktop()
    const [createRoute] = useSlugExtractor([JOBS_ROUTES.CREATE])
    const { hasPermission } = useHasAccess()
    const { user } = useUser()
    const router = useRouter()
    // const isDesktop = useIsDesktop()
    const { jobs: jobsLink } = useNavigationLinks()

    const sublinks = filterUserLinks(jobsLink.subLinks, user)

    return (
        <ViewTypesProvider
            viewTypes={["table", "cards"]}
            viewTypeKey={JOBS_SERIES_LIST_CONTROLS_VIEW}
            defaultViewType={isDesktop ? "table" : "cards"}
        >
            {({ activeViewType }) => (
                <>
                    <NavTopBar.Root>
                        <NavTopBar.Header>
                            <NavTopBar.NavTopBarHeaderStart>
                                <NavTopBar.PageTitle
                                    label={objectMap.job_series.labelPlural}
                                    icon={objectMap.job_series.iconActive}
                                    navigationItems={
                                        sublinks.length > 1
                                            ? sublinks.map((item) => ({
                                                  label: item.label,
                                                  value: item.path,
                                                  isActive: item.label === objectMap.job_series.labelPlural,
                                              }))
                                            : undefined
                                    }
                                    onNavigationItemSelect={(item) => {
                                        router.push(item.value)
                                    }}
                                />
                            </NavTopBar.NavTopBarHeaderStart>
                            <NavTopBar.NavTopBarHeaderEnd>
                                <NavTopBar.PagePresence />
                                {/* TODO: Re-enable create new job series CTA
                                <JobSeriesCreateDialog>
                                    <Button
                                        size={isDesktop ? "md" : "lg"}
                                        variant="solid"
                                        leftIcon={faIcon({ name: "plus", style: "solid" })}
                                        noDebounce={true}
                                    >
                                        {isDesktop ? "New" : null}
                                    </Button>
                                </JobSeriesCreateDialog>
                                */}
                            </NavTopBar.NavTopBarHeaderEnd>
                        </NavTopBar.Header>
                    </NavTopBar.Root>
                    <ObjectsView.Provider
                        activePhaseStorageKey={JOBS_SERIES_LIST_CONTROLS_PHASE}
                        activeTabStorageKey={JOBS_SERIES_LIST_CONTROLS_ACTIVE_TAB}
                        searchKeywordsStorageKey={JOBS_SERIES_LIST_CONTROLS_KEYWORD_SEARCH}
                        listTitle="Job Series"
                        objectType="job_series"
                        objectCreateRoute={createRoute}
                        objectIndexName="JobSeries"
                        objectName="Job Series"
                        defaultActivePhase="open"
                        isRowClickable={hasPermission("jobs_list_permission", PERMISSION_LEVEL.FULL)}
                        isEmptyStateCreateButtonVisible={hasPermission(
                            "jobs_list_permission",
                            PERMISSION_LEVEL.RESTRICTED,
                        )}
                    >
                        <TableView.Provider>
                            <CardsView.Provider
                                attributes={JOB_SERIES_CARDS_VIEW_ATTRIBUTE_OPTIONS}
                                sortOptions={JOB_SERIES_CARDS_VIEW_SORT_OPTIONS}
                            >
                                <ViewBar.Root>
                                    <ViewBar.Start>
                                        <ViewBar.Phases>
                                            <ObjectsView.Phase phase="open" title="Open" />
                                            <ObjectsView.Phase phase="closed" title="Closed" />
                                        </ViewBar.Phases>
                                        <ViewBar.VerticalDivider />
                                        <JobSeriesObjectsViewTabs />
                                    </ViewBar.Start>
                                    <ViewBar.End>
                                        <ObjectsView.SearchInput />
                                        <ViewMenu />
                                    </ViewBar.End>
                                </ViewBar.Root>
                                {activeViewType === "table" && <JobSeriesTableView />}
                                {activeViewType === "cards" && <JobSeriesCardsView />}
                            </CardsView.Provider>
                        </TableView.Provider>
                    </ObjectsView.Provider>
                </>
            )}
        </ViewTypesProvider>
    )
}

export default function JobSeriesList() {
    return (
        <AppFrame.Root collaborationRoomName="job-series-list">
            <JobSeriesListContent />
        </AppFrame.Root>
    )
}
